import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { authService } from '@/services/authService'
import { AuthUser, UserRole } from '@/types/auth'
import { STORAGE_KEYS } from '@/config/env'
import { toast } from 'react-toastify'

interface AuthContextType {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<{ requires2FA: boolean; userId?: string }>
  verify2FA: (userId: string, code: string) => Promise<void>
  toggle2FA: (enable: boolean) => Promise<void>
  logout: () => Promise<void>
  hasRole: (roles: AuthRoleCheck) => boolean
}

type AuthRoleCheck = UserRole | UserRole[]

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const isAuthenticated = !!user

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const profile = await authService.currentUser()
        if (profile) {
          setUser(profile)
        }
      } finally {
        setIsLoading(false)
      }
    }
    if (typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEYS.authToken)) {
      bootstrap()
    } else {
      setIsLoading(false)
    }
  }, [])

  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login({ email, password })
      if (response.requires2FA) {
        return { requires2FA: true, userId: response.userId }
      }
      if (response.user) {
        setUser(response.user)
        toast.success('Login successful')
      }
      return { requires2FA: false }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Login failed')
      throw error
    }
  }

  const verify2FA = async (userId: string, code: string) => {
    try {
      const response = await authService.verify2FA({ userId, code })
      if (response.user) {
        setUser(response.user)
        toast.success('Login successful')
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Verification failed')
      throw error
    }
  }

  const toggle2FA = async (enable: boolean) => {
    try {
      const result = await authService.toggle2FA(enable)
      if (user) {
        setUser({ ...user, twoFactorEnabled: result.twoFactorEnabled })
      }
      toast.success(result.message)
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to update 2FA')
      throw error
    }
  }

  const logout = async () => {
    await authService.logout()
    setUser(null)
    toast.info('You have been logged out')
  }

  const hasRole = (roles: AuthRoleCheck) => {
    if (!user) return false
    const normalizedRoles = Array.isArray(roles) ? roles : [roles]
    return normalizedRoles.includes(user.role)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        verify2FA,
        toggle2FA,
        logout,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

